import {
  atualizarImobilizadoSchema,
  criarImobilizadoSchema,
  type ApropriacaoEntrada,
  type ApropriacaoResponse,
  type ImobilizadoResponse,
  type ImobilizadoTipo,
} from '@triade/contracts';
import { decimalDigitado, decimalParaTextoCampo } from '@/lib/format';

/**
 * Formulario do imobilizado. Strings de tela; o corpo sai pelos schemas do contrato (criarImobilizadoSchema /
 * atualizarImobilizadoSchema). Nenhuma conta aqui: depreciacao e valor vem da API e so sao exibidos.
 */

export const TIPO_ROTULO: Record<ImobilizadoTipo, string> = {
  MAQUINAS_EQUIPAMENTOS: 'Máquinas e equipamentos',
  IMOVEL: 'Imóvel',
  VEICULO: 'Veículo',
};

export interface ImobilizadoFormEntrada {
  tipo: ImobilizadoTipo;
  identificador: string;
  descricao: string;
  valorUnitario: string;
  quantidade: string;
  vidaUtilMeses: string;
  idadeMeses: string;
  /** Data de aquisicao (AAAA-MM-DD, ou vazia = idade manual). Com ela, a idade sai da API. */
  dataAquisicao: string;
  /** Fim da depreciacao (AAAA-MM-DD, ou vazia = sem data limite). Manda sobre a aquisicao e a vida util. */
  depreciarAte: string;
  observacao: string;
  depreciavel: boolean;
  modelo: string;
  marca: string;
  consumoWh: string;
  usoHorasDia: string;
  ativo: boolean;
}

export const IMOBILIZADO_VAZIO: ImobilizadoFormEntrada = {
  tipo: 'MAQUINAS_EQUIPAMENTOS',
  identificador: '',
  descricao: '',
  valorUnitario: '',
  quantidade: '1',
  vidaUtilMeses: '',
  idadeMeses: '',
  dataAquisicao: '',
  depreciarAte: '',
  observacao: '',
  depreciavel: true,
  modelo: '',
  marca: '',
  consumoWh: '',
  usoHorasDia: '',
  ativo: true,
};

/** Registro da API para o formulario (decimais como texto de campo; valor so existe com custo.ver). */
export function deImobilizado(i: ImobilizadoResponse): ImobilizadoFormEntrada {
  return {
    tipo: i.tipo,
    identificador: i.identificador ?? '',
    descricao: i.descricao,
    valorUnitario: decimalParaTextoCampo(i.valorUnitario),
    quantidade: decimalParaTextoCampo(i.quantidade),
    vidaUtilMeses: String(i.vidaUtilMeses),
    // Sem idade informada o campo vem vazio (a API manda 0 nesse caso, que nao e idade).
    idadeMeses: i.idadeInformada ? String(i.idadeMeses) : '',
    dataAquisicao: i.dataAquisicao ? i.dataAquisicao.slice(0, 10) : '',
    depreciarAte: i.depreciarAte ? i.depreciarAte.slice(0, 10) : '',
    observacao: i.observacao ?? '',
    depreciavel: i.depreciavel,
    modelo: i.modelo ?? '',
    marca: i.marca ?? '',
    consumoWh: decimalParaTextoCampo(i.consumoWh),
    usoHorasDia: decimalParaTextoCampo(i.usoHorasDia),
    ativo: i.ativo,
  };
}

/**
 * Apropriacao gravada (resposta da API) como entrada do componente: so muda a forma (centros e fracoes como vieram).
 * Nenhum percentual e recalculado aqui; IGUAL e CRITERIO se resolvem na API.
 */
export function apropriacaoParaEntrada(a: ApropriacaoResponse | null): ApropriacaoEntrada | null {
  if (!a) return null;
  if (a.tipo === 'CRITERIO') return a.criterioId ? { tipo: 'CRITERIO', criterioId: a.criterioId } : null;
  if (a.linhas.length === 0) return null;
  if (a.tipo === 'IGUAL') return { tipo: 'IGUAL', centroIds: a.linhas.map((l) => l.centroId) };
  return { tipo: 'MANUAL', linhas: a.linhas.map((l) => ({ centroId: l.centroId, percentual: l.percentual })) };
}

export type CampoImobilizado = keyof ImobilizadoFormEntrada;

export interface ErroCampoImobilizado {
  campo: CampoImobilizado | null;
  mensagem: string;
}

const MSG_DECIMAL = 'Informe um valor decimal (ex.: 2800 ou 2800,50)';

/** Texto de campo decimal: vazio = omitido; invalido = erro no campo. */
function decimalOpcional(texto: string, campo: CampoImobilizado): { ok: true; valor: string | undefined } | { ok: false; erro: ErroCampoImobilizado } {
  if (texto.trim() === '') return { ok: true, valor: undefined };
  const d = decimalDigitado(texto);
  return d === null ? { ok: false, erro: { campo, mensagem: MSG_DECIMAL } } : { ok: true, valor: d };
}

/**
 * Monta o corpo a partir da tela. `comCusto` = a sessao tem custo.ver (senao valorUnitario nao vai).
 * `apropriacao` null = nao envia (a API nao recebe nada parcial). Consumo e uso so vao para maquinas.
 */
export function paraCorpo(
  v: ImobilizadoFormEntrada,
  opcoes: { comCusto: boolean; apropriacao: ApropriacaoEntrada | null; criando?: boolean },
): { ok: true; corpo: Record<string, unknown> } | { ok: false; erro: ErroCampoImobilizado } {
  const corpo: Record<string, unknown> = {
    tipo: v.tipo,
    descricao: v.descricao.trim(),
    identificador: v.identificador.trim(),
    quantidade: decimalDigitado(v.quantidade) ?? v.quantidade.trim(),
    depreciavel: v.depreciavel,
    modelo: v.modelo.trim(),
    marca: v.marca.trim(),
    ativo: v.ativo,
  };

  const vida = v.vidaUtilMeses.trim();
  if (!/^\d+$/.test(vida)) return { ok: false, erro: { campo: 'vidaUtilMeses', mensagem: 'Informe a vida útil em meses (número inteiro)' } };
  corpo.vidaUtilMeses = Number(vida);

  // Vazio na edicao = limpa (null); na criacao nao vai. Sem calculo: so o texto da tela.
  const ate = v.depreciarAte.trim();
  if (ate) corpo.depreciarAte = ate;
  else if (!opcoes.criando) corpo.depreciarAte = null;
  const obs = v.observacao.trim();
  if (obs) corpo.observacao = obs;
  else if (!opcoes.criando) corpo.observacao = null;

  const data = v.dataAquisicao.trim();
  if (data) {
    // Com data de aquisicao a idade e calculada pela API: o campo manual nao vai.
    corpo.dataAquisicao = data;
  } else {
    if (!opcoes.criando) corpo.dataAquisicao = null;
    // Campo vazio = idade nao informada: a chave nao vai. "0" conta como idade informada.
    const idade = v.idadeMeses.trim();
    if (idade !== '') {
      if (!/^\d+$/.test(idade)) return { ok: false, erro: { campo: 'idadeMeses', mensagem: 'Informe a idade em meses (número inteiro)' } };
      corpo.idadeMeses = Number(idade);
    }
  }

  if (opcoes.comCusto) {
    if (v.valorUnitario.trim() === '') return { ok: false, erro: { campo: 'valorUnitario', mensagem: 'Informe o valor unitário' } };
    const valor = decimalOpcional(v.valorUnitario, 'valorUnitario');
    if (!valor.ok) return valor;
    corpo.valorUnitario = valor.valor;
  }

  if (v.tipo === 'MAQUINAS_EQUIPAMENTOS') {
    const consumo = decimalOpcional(v.consumoWh, 'consumoWh');
    if (!consumo.ok) return consumo;
    if (consumo.valor !== undefined) corpo.consumoWh = consumo.valor;
    const uso = decimalOpcional(v.usoHorasDia, 'usoHorasDia');
    if (!uso.ok) return uso;
    if (uso.valor !== undefined) corpo.usoHorasDia = uso.valor;
  }

  if (opcoes.apropriacao) corpo.apropriacao = opcoes.apropriacao;
  return { ok: true, corpo };
}

/** Valida com o schema do contrato (criar ou atualizar). A primeira issue vira o erro do campo. */
export function validarCorpo(
  corpo: Record<string, unknown>,
  modo: 'criar' | 'atualizar',
): { ok: true; dados: Record<string, unknown> } | { ok: false; erro: ErroCampoImobilizado } {
  const r = modo === 'criar' ? criarImobilizadoSchema.safeParse(corpo) : atualizarImobilizadoSchema.safeParse(corpo);
  if (r.success) return { ok: true, dados: r.data as Record<string, unknown> };
  const i = r.error.issues[0];
  const campo = (i?.path[0] as CampoImobilizado | undefined) ?? null;
  return { ok: false, erro: { campo, mensagem: i?.message ?? 'Dados inválidos' } };
}
